import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnChanges,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MULTI_SELECT_OVERLAY_PANEL_CLASS } from '../../utils/dom-events/dropdown-overlay.util';
import { DropdownOverlayBase } from '../../utils/dropdown-overlay-base/dropdown-overlay-base';
import { IMultiSelectSettings } from './models/multi-select-settings.interface';
import { IOption } from './models/option.interface';

@Component({
  selector: 'sgt-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true
    }
  ]
})
export class MultiSelectComponent
  extends DropdownOverlayBase<number[]>
  implements OnChanges {
  @Input() options: IOption[];
  @Input() settings: IMultiSelectSettings;

  @ContentChild('customOptionTemplate') customOptionTemplate: TemplateRef<{
    $implicit: IOption;
  }>;

  @Output() search = new EventEmitter<boolean>();
  @Output() isFirstOpened = new EventEmitter<boolean>();

  private defaultSettings: IMultiSelectSettings = {
    placeholder: 'Selecione as opções',
    singular: 'Opção',
    plural: 'Opções',
    isDisabled: false,
    isLoading: false,
    emitAfterClosed: false,
    showFooter: true,
    hasSearchField: true,
    searchEmptyResultMessage: 'Nenhuma opção encontrada'
  };

  @ViewChild('inputRef') inputRef: ElementRef<HTMLElement>;
  @ViewChild('optionsList') optionsList: ElementRef<HTMLUListElement>;

  private isFirstOpen = true;

  protected readonly overlayPanelClass = MULTI_SELECT_OVERLAY_PANEL_CLASS;

  protected get triggerElement(): HTMLElement {
    return this.inputRef.nativeElement;
  }

  selectedTotalOptions = 0;
  customPlaceholder = '';
  searchText = '';
  totalOptions = 0;
  filteredOptions: IOption[] = [];

  get semDados(): boolean {
    return !!this.settings?.isLoading || !this.options?.length;
  }

  get totalVisibleOptions(): number {
    return this.filteredOptions.length;
  }

  get selectedVisibleOptions(): number {
    return this.filteredOptions.filter((option) => option.isChecked).length;
  }

  get selectAllLabel(): string {
    return this.searchText
      ? 'Selecionar todos os resultados'
      : 'Selecionar todos';
  }

  get todasVisiveisSelecionadas(): boolean {
    return (
      this.selectedVisibleOptions > 0 &&
      this.selectedVisibleOptions === this.totalVisibleOptions
    );
  }

  get selecaoVisivelParcial(): boolean {
    return (
      this.selectedVisibleOptions > 0 &&
      this.selectedVisibleOptions < this.totalVisibleOptions
    );
  }

  constructor(
    elementRef: ElementRef<HTMLElement>,
    ngZone: NgZone,
    changeDetectorRef: ChangeDetectorRef,
    overlay: Overlay
  ) {
    super(elementRef, ngZone, changeDetectorRef, overlay);
  }

  ngOnChanges(): void {
    this.settings = {
      ...this.defaultSettings,
      ...this.settings
    };
    this.setDisabledPorSettings(!!this.settings.isDisabled);

    if (this.settings?.isLoading) {
      return;
    }

    if (this.options?.length) {
      this.onSearch();
      this.totalSelectedOptions();
    }

    this.changePlaceholder();
  }

  open() {
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.prepararOverlay();
    } else {
      this.closeDropdown();
    }

    if (this.isFirstOpen) {
      this.isFirstOpened.emit(this.isFirstOpen);
      this.isFirstOpen = false;
    }
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.encerrarObservacaoDeScroll();
    if (this.optionsList?.nativeElement) {
      this.optionsList.nativeElement.scrollTop = 0;
    }
  }

  onSearch(): void {
    const text = this.searchText.toLowerCase().trim();

    if (!text) {
      this.filteredOptions = JSON.parse(JSON.stringify(this.options));
      this.getTotalOptions();
      return;
    }

    this.filterOptions(text);
    this.getTotalOptions();
  }

  private filterOptions(text: string): void {
    this.filteredOptions = this.options.filter((option: IOption) =>
      option.label.toLowerCase().includes(text)
    );
  }

  private getTotalOptions(): void {
    this.totalOptions = this.options?.length || 0;
  }

  trackByOptionId(_index: number, option: IOption): number {
    return option.id;
  }

  selectOption(option: IOption, event?: Event) {
    event?.stopPropagation();
    const isChecked =
      (event?.target as HTMLInputElement)?.checked ?? !option.isChecked;
    option.isChecked = isChecked;

    this.syncWithOriginalOptions(option.id, isChecked);

    this.totalSelectedOptions();
    this.changePlaceholder();
    this.emitSelectedValues();
  }

  private syncWithOriginalOptions(optionId: number, isChecked: boolean): void {
    this.options.forEach((option: IOption) => {
      if (option.id === optionId) {
        option.isChecked = isChecked;
      }
    });
  }

  selectAllOptions(event?: Event) {
    event?.stopPropagation();

    const targetGroups = this.searchText ? this.filteredOptions : this.options;
    const allSelected = this.areAllOptionsSelected(targetGroups);
    const newChecked = !allSelected;

    this.setOptionsChecked(targetGroups, newChecked);

    if (!this.searchText) {
      this.setOptionsChecked(this.filteredOptions, newChecked);
    }

    this.totalSelectedOptions();
    this.changePlaceholder();
    this.emitSelectedValues();
  }

  private areAllOptionsSelected(options: IOption[]): boolean {
    return options.every((option) => option.isChecked);
  }

  private setOptionsChecked(options: IOption[], checked: boolean): void {
    options.forEach((option) => {
      option.isChecked = checked;
      if (this.searchText) {
        this.syncWithOriginalOptions(option.id, checked);
      }
    });
  }

  clear() {
    this.selectedTotalOptions = 0;
    this.customPlaceholder = '';
    this.resetFilteredOptions();
    this.resetOptions();
    this.clearSearchFilter();
    this.emitSelectedValues();
    this.closeDropdown();
    this.search.emit(true);
  }

  protected aoFecharPorInteracaoExterna(): void {
    this.checkIfEmitAfterClosed();
  }

  private checkIfEmitAfterClosed(): void {
    this.closeDropdown();
    this.clearSearchFilter();
    this.markAsTouched();

    if (this.settings?.emitAfterClosed) {
      const hasSelectedOptions = this.selectedTotalOptions > 0;
      this.search.emit(hasSelectedOptions);
    }
  }

  clearSearchFilter(event?: Event): void {
    event?.stopPropagation();
    this.searchText = '';
    this.restoreOriginalOptions();
    this.changePlaceholder();
  }

  restoreOriginalOptions(): void {
    this.filteredOptions = JSON.parse(JSON.stringify(this.options));
    this.getTotalOptions();
  }

  resetFilteredOptions(): void {
    this.filteredOptions.forEach(
      (option: IOption) => (option.isChecked = false)
    );
  }

  resetOptions(): void {
    this.options.forEach((option: IOption) => (option.isChecked = false));
  }

  filter() {
    this.search.emit(true);
    this.closeDropdown();
    this.clearSearchFilter();
  }

  totalSelectedOptions(): void {
    this.selectedTotalOptions = this.options.filter(
      (option) => option.isChecked
    ).length;
  }

  changePlaceholder(): void {
    this.customPlaceholder = this.selectedTotalOptions
      ? this.settings?.plural ?? 'opções selecionadas'
      : this.settings?.placeholder;
  }

  protected updateValue(value: number[] | null): void {
    if (!this.options) {
      return;
    }

    const selectedIds = value || [];

    this.options.forEach((option) => {
      option.isChecked = selectedIds.includes(option.id);
    });

    if (this.filteredOptions) {
      this.filteredOptions.forEach((option) => {
        option.isChecked = selectedIds.includes(option.id);
      });
    }

    this.totalSelectedOptions();
    this.changePlaceholder();
  }

  private emitSelectedValues(): void {
    const selectedIds = this.getSelectedOptionIds();
    this.emitChange(selectedIds);
  }

  private getSelectedOptionIds(): number[] {
    return this.options
      ? this.options
          .filter((option) => option.isChecked)
          .map((option) => option.id)
      : [];
  }
}
